import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage } from "aws-amplify/utils";

/**
 * Derives the narrowest cookie domain that still covers all three apps
 * (main, enterprise, inventory) for the current branch.
 *
 * Prod: .assetwatch.com  (app / enterprise / inventory are direct subdomains)
 * Non-prod: .{branch}.{env}.assetwatch.com  (scoped to the branch so cookies
 *           don't leak to storybook or other branches)
 */
function cookieDomain(env: string | undefined): string | undefined {
  if (env === "prod" || env === "qa-legacy") return ".assetwatch.com";

  const { hostname } = window.location;
  const parts = hostname.split(".");

  // enterprise.branch.env.assetwatch.com → 5 parts → .branch.env.assetwatch.com
  if (parts.length >= 5) return "." + parts.slice(1).join(".");

  // branch.env.assetwatch.com → 4 parts → .branch.env.assetwatch.com
  if (parts.length === 4) return "." + hostname;

  // localhost or unexpected — no cross-domain sharing
  return undefined;
}

/**
 * Configures Amplify token storage to use a parent-domain cookie so that
 * auth is shared across all AssetWatch apps on the same subdomain tree.
 * Call this immediately after Amplify.configure() in each app's entry point,
 * passing import.meta.env.VITE_DEPLOY_ENVIRONMENT as the env argument.
 */
export function configureSharedCookieAuth(env: string | undefined): void {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      domain: cookieDomain(env),
      secure: window.location.protocol === "https:",
      sameSite: "lax",
      expires: 30,
    }),
  );
}
